import React, { useEffect } from "react";
import ProductRowItem from "./ProductRowItem";
import { ProductRow } from "../../types";
import { getReportYears } from "@/ReactInit/Scripts/DashboardPage/utils/reportYears";
import {
  cn,
  normalizeProductKey,
} from "@/ReactInit/Scripts/DashboardPage/utils/dashboardHelpers";

interface Props {
  rows: ProductRow[];
  setRows: (rows: ProductRow[]) => void;
  searchInputs: string[];
  setSearchInputs: (arr: string[]) => void;
  category: string;
  categoryProducts: string[];
  showLastYear: boolean;
  setShowLastYear: (v: boolean) => void;
  onProductChange: (rowIndex: number, product: string) => void;
  onVolumeChange: (rowIndex: number, volume: string) => void;
  onLastYearVolumeChange?: (rowIndex: number, value: number | null) => void;
  onRowErrorStateChange: (rowIndex: number, hasError: boolean) => void;
  lastYearVolumesByProduct: Record<string, number>;
  takenProducts?: string[];
  reportYear?: number;
  compareYear?: number;
  disabled?: boolean;
}

const ProductRows: React.FC<Props> = ({
  rows,
  setRows,
  searchInputs,
  setSearchInputs,
  categoryProducts,
  showLastYear,
  setShowLastYear,
  onProductChange,
  onVolumeChange,
  onLastYearVolumeChange,
  onRowErrorStateChange,
  lastYearVolumesByProduct,
  takenProducts = [],
  reportYear,
  compareYear,
  disabled = false,
}) => {
  const { currentYear, previousYear } = getReportYears();
  const thisYearLabel = reportYear ? reportYear : currentYear;
  const lastYearLabel = compareYear ? compareYear : previousYear;

  useEffect(() => {
    if (showLastYear) return;

    const shouldShow = rows.some((r) => {
      if (r.lastYearVolume != null) return true;
      const key = normalizeProductKey(r.product ?? "");
      return key && typeof lastYearVolumesByProduct[key] === "number";
    });

    if (shouldShow) setShowLastYear(true);
  }, [rows, lastYearVolumesByProduct, showLastYear, setShowLastYear]);

  const addRow = () => {
    setRows([...rows, { product: "", volume: "", lastYearVolume: null }]);
    setSearchInputs([...searchInputs, ""]);
  };

  return (
    <div className="flex flex-col gap-2">
      {/* Labels */}
      <div className="flex">
        <legend className="w-full px-1 pt-3 pb-2">
          <p className="text-lg font-semibold tablet:text-xl">
            Produkt <span className="leading-normal text-red-secondary">*</span>
          </p>

          <span className="hidden text-base leading-6 text-black-subtile tablet:inline">
            Skriv inn eller velg produkt
          </span>
        </legend>

        <legend className="w-full px-1 pt-3 pb-2">
          <p className="text-lg font-semibold tablet:text-xl">
            Volum i {thisYearLabel}
            <span className="ml-1 leading-normal text-red-secondary">*</span>
          </p>
          <span className="hidden text-base leading-6 text-black-subtile tablet:inline">
            Skriv inn tallene (i kg)
          </span>
        </legend>

        {showLastYear && (
          <legend className="w-full px-1 pt-3 pb-2 max-w-[70px] tablet:max-w-[200px]">
            <p className="text-lg font-semibold tablet:text-xl">
              Volum i {lastYearLabel}
            </p>
            <span className="hidden text-base leading-6 text-black-subtile tablet:inline">
              Vises automatisk ved tilgjengelige data
            </span>
          </legend>
        )}
      </div>

      {/* Product rows */}
      <div className="divide-y divide-disabled-bg border-y border-disabled-bg">
        {rows.map((row, index) => (
          <ProductRowItem
            key={index}
            index={index}
            row={row}
            rows={rows}
            setRows={setRows}
            searchInputs={searchInputs}
            setSearchInputs={setSearchInputs}
            categoryProducts={categoryProducts}
            showLastYear={showLastYear}
            setShowLastYear={setShowLastYear}
            onProductChange={onProductChange}
            onVolumeChange={onVolumeChange}
            onLastYearVolumeChange={onLastYearVolumeChange}
            onRowErrorStateChange={onRowErrorStateChange}
            lastYearVolumesByProduct={lastYearVolumesByProduct}
            takenProducts={(takenProducts ?? []).filter(
              (k) => k !== row.product.trim().toLowerCase()
            )}
            disabled={disabled}
          />
        ))}
      </div>

      <button
        type="button"
        onClick={addRow}
        disabled={disabled}
        className={cn(
          "mx-auto btn-ghost group",
          disabled && "opacity-50 cursor-not-allowed"
        )}
      >
        <span className="icon-plus" />
        <span className="group-hover:underline">Legg til produkt</span>
      </button>
    </div>
  );
};

export default ProductRows;
